/* eslint-disable complexity */

import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import { FileUploadStatusProps } from 'carbon-react/lib/components/file-input';

import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { ExcelUploadPanel } from '../form-elements/excel-upload-panel/excel-upload-panel';
import { ForecastDatasource, ForecastDatasourceProps } from '../form-elements/forecast-datasource';
import { ForecastDuration, ForecastDurationProps } from '../form-elements/forecast-duration';
import { ForecastFormErrors, ForecastTypeOption } from '../../types';
import { ForecastFrom, ForecastFromProps } from '../form-elements/forecast-from';
import { ForecastName, ForecastNameProps } from '../form-elements/forecast-name';
import { ForecastPreviousActuals, ForecastPreviousActualsProps } from '../form-elements/forecast-previous-actuals';
import { ForecastSourceForecast, ForecastSourceForecastProps } from '../form-elements/forecast-source-forecast';
import { ForecastType, ForecastTypeProps } from '../form-elements/forecast-type';
import { NewForecastDataSource, OrgOrConForecastListItem } from '../../../dashboard/types';

import styles from '../form-elements/elements.scss';
import { IconMessage } from '../form-elements/icon-message/icon-message';

interface CreateNewForecastFormProps {
  datasource: NewForecastDataSource | '';
  durationFixed: string;
  durationRolling: string;
  errors?: ForecastFormErrors;
  excelFileError?: string;
  excelTemplateLink?: string;
  fileUploadStatus?: FileUploadStatusProps;
  forecasts?: OrgOrConForecastListItem[];
  from: string;
  isLoadingExcelTemplate?: boolean;
  maxDate?: string;
  minDate?: string;
  name: string;
  previousActuals?: boolean;
  sourceForecast?: string;
  type?: ForecastTypeOption;
  onChangeDatasource: ForecastDatasourceProps['onChange'];
  onChangeDurationFixed: ForecastDurationProps['onChange'];
  onChangeDurationRolling: ForecastDurationProps['onChange'];
  onChangeFile: (files: FileList) => void;
  onChangeFrom: ForecastFromProps['onChange'];
  onChangeName: ForecastNameProps['onChange'];
  onChangePreviousActuals: ForecastPreviousActualsProps['onChange'];
  onChangeSourceForecast: ForecastSourceForecastProps['onChange'];
  onChangeType: ForecastTypeProps['onChange'];
  revalidate: () => void;
}

export const CreateNewForecastForm = ({
  errors = {},
  datasource,
  durationFixed,
  durationRolling,
  excelFileError,
  excelTemplateLink,
  fileUploadStatus,
  forecasts,
  from,
  isLoadingExcelTemplate,
  maxDate,
  minDate,
  name,
  previousActuals,
  sourceForecast,
  type,
  onChangeDatasource,
  onChangeDurationFixed,
  onChangeDurationRolling,
  onChangeFile,
  onChangeFrom,
  onChangeName,
  onChangePreviousActuals,
  onChangeSourceForecast,
  onChangeType,
  revalidate
}: CreateNewForecastFormProps) => {
  const intl = useIntl();

  return (
    <Box className={styles.carbonFormFixes}>
      <WizardRequiredFieldInfo />

      <ForecastName
        value={name}
        onChange={onChangeName}
        label={intl.formatMessage({ id: 'predict.create_forecast.forecast_name', defaultMessage: 'Forecast name' })}
        error={errors.name?.message}
        onBlur={revalidate}
      />

      <ForecastType
        value={type || ''}
        onChange={onChangeType}
        error={errors.type?.message}
        onBlur={revalidate}
        fixedContent={
          <>
            <ForecastFrom
              maxDate={maxDate}
              minDate={minDate}
              value={from}
              onChange={onChangeFrom}
              label={intl.formatMessage({
                id: 'predict.create_forecast.start_forecast_from',
                defaultMessage: 'Start forecast from'
              })}
              error={errors.from?.message}
              disabled={type === 'rolling'}
              onBlur={revalidate}
            />
            <ForecastDuration
              type={type || ''}
              value={durationFixed}
              onChange={onChangeDurationFixed}
              legend={intl.formatMessage({ id: 'predict.create_forecast.duration', defaultMessage: 'Duration' })}
              error={errors.duration?.message}
              onBlur={revalidate}
              mb={0}
            />
          </>
        }
        rollingContent={
          <>
            <IconMessage mb={3}>
              {intl.formatMessage({
                id: 'predict.create_forecast.forecast_data_always_starts_from_the_last_completed_sync',
                defaultMessage: 'Forecast data always starts from the last completed sync'
              })}
            </IconMessage>
            <ForecastDuration
              type={type || ''}
              value={durationRolling}
              onChange={onChangeDurationRolling}
              legend={intl.formatMessage({ id: 'predict.create_forecast.duration', defaultMessage: 'Duration' })}
              error={errors.duration?.message}
              onBlur={revalidate}
            />
            <ForecastPreviousActuals
              checked={!!previousActuals}
              onChange={onChangePreviousActuals}
              error={errors.previousActuals?.message}
              onBlur={revalidate}
              mb={0}
            />
          </>
        }
      />

      <ForecastDatasource
        type="forecast"
        value={datasource}
        onChange={onChangeDatasource}
        error={errors.datasource?.message}
        onBlur={revalidate}
      />

      {datasource === NewForecastDataSource.FromDuplicate && (
        <ForecastSourceForecast
          type="forecast"
          value={sourceForecast ?? ''}
          onChange={onChangeSourceForecast}
          error={errors.sourceForecast?.message}
          forecasts={forecasts}
          onBlur={revalidate}
        />
      )}

      {datasource === NewForecastDataSource.FromExcelUpload && (
        <ExcelUploadPanel
          error={excelFileError}
          isLoading={isLoadingExcelTemplate}
          onChange={onChangeFile}
          templateLink={excelTemplateLink}
          uploadStatus={fileUploadStatus}
        />
      )}
    </Box>
  );
};
